'use client'

import { cn } from '@/lib/utils'
import { AnimatedNumber } from '@/components/ui/animated-number'

import { EASE, HAIRLINE, useReveal } from './shared'

/* The four platform figures, each fused to the sentence that earns it. The
   digits are server-rendered at their true values and count up (shared
   AnimatedNumber) once the band is in view — each cell's rule still draws in
   before its number settles. */

const METRICS = [
  {
    value: '100%',
    claim: 'Module integration',
    proof:
      'There is no connector layer to maintain, because there is nothing to connect.',
  },
  {
    value: '6×',
    claim: 'Faster decision cycles',
    proof:
      'Leadership reads the institution’s position directly instead of waiting on collation.',
  },
  {
    value: '85%',
    claim: 'Fewer data silos',
    proof: 'One record, one place, one owner, one audit trail.',
  },
  {
    value: '99.9%',
    claim: 'System uptime',
    proof:
      'Multi-region replication, measured across the production estate, not a target.',
  },
]

export function UdcMetrics() {
  const band = useReveal<HTMLDivElement>(0.2)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <div ref={band.ref} className={cn('border-t', HAIRLINE)}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {METRICS.map((m, i) => (
              <div key={m.claim} className="relative py-7 pr-6 sm:py-9">
                {/* The rule between cells draws downward before the figure
                    resolves. It is the only motion in the band. */}
                <span
                  aria-hidden
                  className="absolute bottom-0 left-0 top-0 hidden w-px origin-top bg-border/70 sm:block"
                  style={{
                    left: '-1px',
                    transform: band.shown ? 'scaleY(1)' : 'scaleY(0)',
                    transition: `transform 620ms ${EASE} ${i * 110}ms`,
                    opacity: i === 0 ? 0 : 1,
                  }}
                />

                <div
                  className="sm:pl-6 lg:pl-8"
                  style={{
                    opacity: band.shown ? 1 : 0,
                    transform: band.shown ? 'none' : 'translateY(8px)',
                    transition: `opacity 520ms ${EASE} ${180 + i * 110}ms, transform 520ms ${EASE} ${180 + i * 110}ms`,
                  }}
                >
                  <dd className="font-heading text-4xl font-normal tabular-nums leading-none text-foreground sm:text-5xl">
                    <AnimatedNumber value={m.value} delay={180 + i * 110} />
                  </dd>
                  <dt className="mt-3 text-[13px] font-medium text-foreground sm:text-sm">
                    {m.claim}
                  </dt>
                  <p className="mt-2 max-w-[34ch] text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
                    {m.proof}
                  </p>
                </div>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  )
}
